import type { ReactNode } from 'react';
import { resolveImagePath } from '@/utils/imageResolver';

interface FooterLinkProps {
  href: string;
  children: ReactNode;
  className?: string;
}

function FooterLink({ href, children, className = "" }: FooterLinkProps) {
  return (
    <a
      href={href}
      className={`block py-2 px-4 text-white font-medium transition-colors hover:bg-white hover:bg-opacity-10 ${className}`}
    >
      {children}
    </a>
  );
}

interface SocialLinkProps {
  href: string;
  children: ReactNode;
  ariaLabel: string;
}

function SocialLink({ href, children, ariaLabel }: SocialLinkProps) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="bg-gray-700 hover:bg-gray-600 transition-colors p-3 inline-flex items-center justify-center"
      aria-label={ariaLabel}
    >
      {children}
    </a>
  );
}

export function Footer() {
  return (
    <footer
      className="relative bg-cover bg-center bg-no-repeat"
      style={{
        backgroundImage: `url(${resolveImagePath('/images/footer-bg.jpg')})`,
      }}
    >
      {/* Background overlay */}
      <div className="absolute inset-0 bg-black bg-opacity-50"></div>
      
      {/* Footer content */}
      <div className="relative z-10">
        {/* Main footer section */}
        <div className="container mx-auto px-4 py-16">
          <div className="flex flex-wrap items-start justify-center lg:justify-between gap-8">
            
            {/* Logo section */}
            <div className="flex-shrink-0">
              <a href="/" className="inline-block">
                <img
                  src={resolveImagePath('/images/logo.png')}
                  alt="American Aviation Flight Academy"
                  className="h-20 w-auto"
                />
              </a>
            </div>

            {/* Navigation sections */}
            <div className="flex flex-wrap gap-0">
              {/* First column */}
              <div className="flex flex-col">
                <FooterLink href="/about-us/" className="bg-blue-800">
                  About Us
                </FooterLink>
                <FooterLink href="/courses/" className="bg-red-600">
                  Our Packages
                </FooterLink>
                <FooterLink href="/our-fleet/" className="bg-red-700">
                  Our Fleet
                </FooterLink>
              </div>

              {/* Second column */}
              <div className="flex flex-col">
                <FooterLink href="/helpful-links/" className="bg-purple-700">
                  Helpful Links
                </FooterLink>
                <FooterLink href="/category/news/" className="bg-purple-600">
                  Recent News
                </FooterLink>
                <FooterLink href="/contact-us/" className="bg-red-600">
                  Contact Us
                </FooterLink>
              </div>
            </div>

            {/* Social media section */}
            <div className="text-center">
              <h3 className="text-white font-bold text-xl mb-6 border-b-2 border-blue-600 pb-2">
                Let's Connect
              </h3>
              <div className="flex gap-2">
                <SocialLink
                  href="https://www.facebook.com/americanaviationflightacademy/"
                  ariaLabel="Facebook"
                >
                  <svg className="w-6 h-6 text-white fill-current" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </SocialLink>

                <SocialLink
                  href="https://www.google.com/search?rlz=1C5CHFA_enUS770US770&biw=1851&bih=898&ei=fuYCXL--H8uxggeDjZ0Q&q=american2aa99e07529fde700cd6b8d10c03e1466d85e40d49ea8fce181ed2a2f6593f7720aviation&oq=american+aviation&gs_l=psy-ab.3.1.35i39l2j0i67j0l7.2184.2184..2328...0.0..0.105.105.0j1......0....1..gws-wiz.Ph_8RIWDgss&npsic=0&rflfq=1&rlha=0&rllag=28476379,-82446124,583&tbm=lcl&rldimm=6886397213748437141&ved=2ahUKEwjD8KLYtP_eAhXxm-AKHW-ID6wQvS4wAXoECAAQIQ&rldoc=1&tbs=lrf:!2m4!1e17!4m2!17m1!1e2!2m1!1e2!3sIAE,lf:1,lf_ui:2#rlfi=hd:;si:6886397213748437141;mv:!1m2!1d28.508726719360716!2d-82.41509078808593!2m2!1d28.45191773819614!2d-82.5379145032959!4m2!1d28.480326048485498!2d-82.47650264569091!5i14"
                  ariaLabel="Google"
                >
                  <svg className="w-6 h-6 text-white fill-current" viewBox="0 0 24 24">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                </SocialLink>

                <SocialLink
                  href="https://www.instagram.com/americanaviationflightacademy/"
                  ariaLabel="Instagram"
                >
                  <svg className="w-6 h-6 text-white fill-current" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </SocialLink>
              </div>
            </div>
          </div>
        </div>

        {/* Copyright section */}
        <div className="bg-gray-800 bg-opacity-80">
          <div className="container mx-auto px-4 py-4">
            <p className="text-center text-gray-300 text-sm">
              ©2026 American Aviation
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}